var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Controller } from "react-hook-form";
import { Divider, FormControl, FormControlLabel, FormHelperText, FormLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Slider, TextField, Typography, } from "@mui/material";
import { countries } from "./countries";
var POSITION_CHOICES = [
    "GK",
    "LB",
    "LWB",
    "RB",
    "RWB",
    "LCB",
    "RCB",
    "CB",
    "SW",
    "CDM",
    "CM",
    "LCM",
    "RCM",
    "LM",
    "RM",
    "LAM",
    "RAM",
    "CAM",
    "LW",
    "RW",
    "CF",
    "ST",
];
var FEET_CHOICES = ["Left", "Right"];
var ATTRIBUTES = [
    { name: "speed", label: "Speed" },
    { name: "fitness", label: "Fitness" },
    { name: "keeping", label: "Goalkeeping" },
    { name: "passing", label: "Passing" },
    { name: "finishing", label: "Finishing" },
    { name: "defending", label: "Defending" },
    { name: "positioning", label: "Positioning" },
    { name: "heading", label: "Heading" },
    { name: "dribbling", label: "Dribbling" },
    { name: "shooting", label: "Shooting" },
    { name: "tackling", label: "Tackling" },
    { name: "crossing", label: "Crossing" },
    { name: "communication", label: "Communication" },
    { name: "leadership", label: "Leadership" },
];
var getAttributeColor = function (value) {
    if (value >= 80)
        return "#4caf50";
    if (value >= 60)
        return "#2196f3";
    if (value >= 40)
        return "#ff9800";
    return "#f44336";
};
/**
 * Nationality and bio. Split out so onboarding can show just these to a
 * non-playing manager and collapse the rest.
 */
var PlayerBioFields = function (_a) {
    var control = _a.control, _b = _a.namePrefix, namePrefix = _b === void 0 ? "" : _b, _c = _a.required, required = _c === void 0 ? true : _c;
    var path = function (name) { return "".concat(namePrefix).concat(name); };
    return (_jsxs(_Fragment, { children: [_jsx(Divider, { style: { margin: "15px 0" } }), _jsx(Controller, { name: path("nationality"), control: control, rules: required ? { required: "Nationality is required" } : {}, render: function (_a) {
                    var field = _a.field, fieldState = _a.fieldState;
                    return (_jsxs(FormControl, { fullWidth: true, margin: "normal", required: required, error: !!fieldState.error, children: [_jsx(InputLabel, { id: "country-select-label", children: "Nationality" }), _jsx(Select, __assign({}, field, { labelId: "country-select-label", label: "Nationality", children: countries.map(function (country) { return (_jsx(MenuItem, { value: country.code, children: country.name }, country.code)); }) })), fieldState.error && (_jsx(FormHelperText, { children: fieldState.error.message }))] }));
                } }), _jsx(Controller, { name: path("bio"), control: control, render: function (_a) {
                    var field = _a.field;
                    return (_jsx(TextField, __assign({}, field, { margin: "normal", fullWidth: true, label: "Bio", multiline: true, rows: 3 })));
                } })] }));
};
/**
 * Primary/secondary position and preferred foot — the player-specific
 * positioning fields.
 */
var PlayerPositionFields = function (_a) {
    var control = _a.control, _b = _a.namePrefix, namePrefix = _b === void 0 ? "" : _b, _c = _a.required, required = _c === void 0 ? true : _c;
    var path = function (name) { return "".concat(namePrefix).concat(name); };
    return (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "h6", style: { marginTop: "20px" }, children: "Positioning" }), _jsx(Divider, { style: { margin: "15px 0" } }), _jsx(Controller, { name: path("positionOne"), control: control, rules: required ? { required: "Primary position is required" } : {}, render: function (_a) {
                    var field = _a.field, fieldState = _a.fieldState;
                    return (_jsxs(FormControl, { fullWidth: true, margin: "normal", required: required, error: !!fieldState.error, children: [_jsx(InputLabel, { children: "Primary Position" }), _jsx(Select, __assign({}, field, { label: "Primary Position", children: POSITION_CHOICES.map(function (p) { return (_jsx(MenuItem, { value: p, children: p }, p)); }) })), fieldState.error && (_jsx(FormHelperText, { children: fieldState.error.message }))] }));
                } }), _jsxs(FormControl, { fullWidth: true, margin: "normal", children: [_jsx(InputLabel, { children: "Secondary Position" }), _jsx(Controller, { name: path("positionTwo"), control: control, render: function (_a) {
                            var field = _a.field;
                            return (_jsxs(Select, __assign({}, field, { label: "Secondary Position", children: [_jsx(MenuItem, { value: "", children: _jsx("em", { children: "None" }) }), POSITION_CHOICES.map(function (p) { return (_jsx(MenuItem, { value: p, children: p }, p)); })] })));
                        } })] }), _jsx(Controller, { name: path("foot"), control: control, rules: required ? { required: "Preferred foot is required" } : {}, render: function (_a) {
                    var field = _a.field, fieldState = _a.fieldState;
                    return (_jsxs(FormControl, { margin: "normal", required: required, error: !!fieldState.error, children: [_jsx(FormLabel, { children: "Preferred Foot" }), _jsx(RadioGroup, __assign({}, field, { row: true, children: FEET_CHOICES.map(function (f) { return (_jsx(FormControlLabel, { value: f, control: _jsx(Radio, {}), label: f }, f)); }) })), fieldState.error && (_jsx(FormHelperText, { children: fieldState.error.message }))] }));
                } })] }));
};
/**
 * The 0-100 attribute sliders. Optional deeper step in onboarding (progressive
 * disclosure) so new users aren't overwhelmed by 14 sliders up front.
 */
var PlayerAttributeFields = function (_a) {
    var control = _a.control, _b = _a.namePrefix, namePrefix = _b === void 0 ? "" : _b;
    var path = function (name) { return "".concat(namePrefix).concat(name); };
    return (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "h6", style: { marginTop: "20px" }, children: "Attributes" }), _jsx(Divider, { style: { margin: "15px 0" } }), ATTRIBUTES.map(function (attr) { return (_jsx(Controller, { name: path(attr.name), control: control, render: function (_a) {
                    var field = _a.field;
                    return (_jsxs(_Fragment, { children: [_jsx(Typography, { children: attr.label }), _jsx(Slider, __assign({}, field, { min: 0, max: 100, valueLabelDisplay: "auto", sx: { color: getAttributeColor(field.value) } }))] }));
                } }, attr.name)); })] }));
};
/**
 * Renders the full set of writable Player metadata inputs (nationality, bio,
 * positions, foot, and the 0-100 attribute sliders). Shared so the offline
 * player create form presents the same fields as the player profile form.
 */
export var PlayerMetadataFields = function (_a) {
    var control = _a.control, _b = _a.namePrefix, namePrefix = _b === void 0 ? "" : _b;
    return (_jsxs(_Fragment, { children: [_jsx(PlayerBioFields, { control: control, namePrefix: namePrefix }), _jsx(PlayerPositionFields, { control: control, namePrefix: namePrefix }), _jsx(PlayerAttributeFields, { control: control, namePrefix: namePrefix })] }));
};
